import { PLAYBACK_DELAY_S } from './live-playback';
import { toCents, twapCents } from './twap';

export type Token = 'DPN' | 'DPS';
/**
 * How a round settles (ADR 095, spec D6). kline1m: the 1m kline open and close, a tie is Down. twap60: the 60 s
 * means before startAt and before endAt (twap.ts), a tie is Up. A round without the field is kline1m.
 */
export type SettleMethod = 'kline1m' | 'twap60';
const SETTLE_METHODS: readonly unknown[] = ['kline1m', 'twap60'] satisfies SettleMethod[];
export type Candle = { time: number; open: number; high: number; low: number; close: number };
export type Round = {
  id: string;
  startAt: number;
  endAt: number;
  status: 'scheduled' | 'open' | 'pending' | 'resolved' | 'cancelled';
  referencePrice?: number;
  finalPrice?: number;
  result?: 'up' | 'down';
  settleMethod?: SettleMethod;
  markets: Partial<Record<Token, { eventId: string; ready: boolean; marketAddress?: string }>>;
};
/** `settleMethodSince`: startAt of the first twap60 round (engine config), for the rule banner. Optional. */
export type RoundFeed = { serverTime: number; timezone: string; rounds: Round[]; settleMethodSince?: number };
export type CandleFeed = {
  roundId: string;
  source: string;
  updatedAt: number;
  staleAfterSeconds: number;
  candles: Candle[];
};
/** One 1s BTC price of the tick feed: [epoch second, price]. */
export type TickPoint = [time: number, price: number];
/** /short-markets/ticks.json (last 900s) and ticks-tail.json (last 30s). A reference line, never a settlement price. */
export type TickFeed = { source: string; updatedAt: number; staleAfterSeconds: number; points: TickPoint[] };
/** The price chart shows the last 15 minutes up to the round's end. */
export const TICK_WINDOW_S = 900;
/** A round's tick line is complete once it reaches this close to endAt. */
export const TICK_COMPLETE_S = 5;
const ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const epoch = (v: unknown): v is number => Number.isInteger(v) && Number(v) >= 0 && Number(v) < 1e11;
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
/** The settle method of a round: twap60 only for exactly "twap60"; a missing (D6) or foreign value is kline1m. */
export const settleMethodOf = (round: Pick<Round, 'settleMethod'>): SettleMethod =>
  round.settleMethod === 'twap60' ? 'twap60' : 'kline1m';
/**
 * Phase of a round. The engine's `status` decides (it writes open iff startAt <= now < endAt and
 * pending after endAt, every ~5s tick); the page clock only fills in a 'scheduled' round whose
 * start or end passed before the engine's next tick.
 */
export const roundPhase = (round: Round, now: number) => {
  if (round.status !== 'scheduled') return round.status;
  if (now < round.startAt) return 'upcoming';
  return now < round.endAt ? 'open' : 'pending';
};
export type RoundPhase = ReturnType<typeof roundPhase>;
export const selectDefaultRound = (rounds: Round[], now: number) => {
  const ordered = [...rounds].sort((a, b) => a.startAt - b.startAt);
  return (
    ordered.find(r => r.startAt <= now && now < r.endAt && r.status !== 'cancelled') ??
    ordered.find(r => r.startAt > now && r.status !== 'cancelled') ??
    ordered.at(-1)
  );
};
/**
 * The page follows the next round at the latest this many seconds after endAt, also when the feed still marks the ended
 * round open (the engine writes rounds.json once per tick, about 15 s at a close). 8 s: the chart playback ends then.
 */
export const FOLLOW_NEXT_AFTER_CLOSE_S = PLAYBACK_DELAY_S + 2;
/**
 * Round the page follows when the reader has not picked one: the open round as soon as there
 * is one (one the engine marks open first, then one only the clock opens); otherwise it stays on
 * `previous` (an ended round keeps showing its settlement, never jumping to a round that has not
 * started), and re-picks the default when `previous` left the feed.
 * A round the engine marks open counts until its endAt + FOLLOW_NEXT_AFTER_CLOSE_S, but not past its endAt once
 * `previous` starts at or after that endAt (the reader chose a later round, or the page moved on: `left`).
 */
export const followRound = (rounds: Round[], now: number, previous: string) => {
  const chosen = rounds.find(r => r.id === previous);
  const left = (r: Round) => now >= r.endAt && !!chosen && chosen.startAt >= r.endAt;
  const marked = rounds.find(r => r.status === 'open' && now < r.endAt + FOLLOW_NEXT_AFTER_CLOSE_S && !left(r));
  const clockOpen = rounds.find(r => r.status === 'scheduled' && r.startAt <= now && now < r.endAt);
  return marked?.id ?? clockOpen?.id ?? (chosen ? previous : (selectDefaultRound(rounds, now)?.id ?? ''));
};
export const roundWindow = (rounds: Round[], selected: string, count = 7) => {
  const index = Math.max(
    0,
    rounds.findIndex(r => r.id === selected)
  );
  const start = Math.min(Math.max(0, index - Math.floor(count / 2)), Math.max(0, rounds.length - count));
  return rounds.slice(start, start + count);
};
export const aggregateCandles = (candles: Candle[], interval: number): Candle[] => {
  if (!Number.isInteger(interval) || interval < 60) throw new Error('Invalid candle interval');
  const unique = new Map<number, Candle>();
  for (const c of candles) {
    if (
      !epoch(c.time) ||
      ![c.open, c.high, c.low, c.close].every(positive) ||
      c.low > Math.min(c.open, c.close) ||
      c.high < Math.max(c.open, c.close) ||
      c.low > c.high
    )
      throw new Error('Invalid OHLC');
    unique.set(c.time, c);
  }
  const buckets = new Map<number, Candle>();
  for (const c of [...unique.values()].sort((a, b) => a.time - b.time)) {
    const time = Math.floor(c.time / interval) * interval;
    const prior = buckets.get(time);
    buckets.set(
      time,
      prior
        ? { time, open: prior.open, high: Math.max(prior.high, c.high), low: Math.min(prior.low, c.low), close: c.close }
        : { ...c, time }
    );
  }
  return [...buckets.values()];
};
export const parseRoundFeed = (value: unknown): RoundFeed => {
  const data = value as RoundFeed;
  if (
    !data ||
    !epoch(data.serverTime) ||
    typeof data.timezone !== 'string' ||
    !Array.isArray(data.rounds) ||
    (data.settleMethodSince !== undefined && !epoch(data.settleMethodSince))
  )
    throw new Error('Invalid round feed');
  new Intl.DateTimeFormat('en', { timeZone: data.timezone });
  const ids = new Set<string>();
  for (const r of data.rounds) {
    if (
      !r ||
      typeof r.id !== 'string' ||
      !r.id ||
      ids.has(r.id) ||
      !epoch(r.startAt) ||
      !epoch(r.endAt) ||
      r.startAt >= r.endAt ||
      !['scheduled', 'open', 'pending', 'resolved', 'cancelled'].includes(r.status) ||
      // An unknown settle method is a schema change, like an unknown status: the whole feed is invalid.
      (r.settleMethod !== undefined && !SETTLE_METHODS.includes(r.settleMethod)) ||
      !r.markets
    )
      throw new Error('Invalid round');
    for (const token of ['DPN', 'DPS'] as const) {
      const m = r.markets[token];
      if (m && (typeof m.eventId !== 'string' || !m.eventId || typeof m.ready !== 'boolean'))
        throw new Error('Invalid market');
      // On-chain pool address is optional (the market may not exist yet) but never half-valid.
      if (m && m.marketAddress !== undefined && !ADDRESS.test(String(m.marketAddress)))
        throw new Error('Invalid market address');
    }
    if (
      (r.referencePrice !== undefined && !positive(r.referencePrice)) ||
      (r.finalPrice !== undefined && !positive(r.finalPrice))
    )
      throw new Error('Invalid reference price');
    ids.add(r.id);
  }
  return { ...data, rounds: [...data.rounds].sort((a, b) => a.startAt - b.startAt) };
};
export const parseCandleFeed = (value: unknown, roundId: string): CandleFeed => {
  const data = value as CandleFeed;
  if (
    !data ||
    data.roundId !== roundId ||
    !data.source ||
    !epoch(data.updatedAt) ||
    !positive(data.staleAfterSeconds) ||
    !Array.isArray(data.candles)
  )
    throw new Error('Invalid candle feed');
  return { ...data, candles: aggregateCandles(data.candles, 60) };
};
/**
 * Strict: any malformed field or a time that does not move forward throws, and the page keeps the candles.
 * A file of another shape throws an error named InvalidTickFeed (the poll treats it as permanent); a bad
 * point throws a plain Error (a torn write may heal on the next read).
 */
export const parseTickFeed = (value: unknown): TickFeed => {
  const data = value as TickFeed;
  if (
    !data ||
    typeof data.source !== 'string' ||
    !data.source ||
    !epoch(data.updatedAt) ||
    !positive(data.staleAfterSeconds) ||
    data.staleAfterSeconds > 60 || // a feed that is never stale would keep a stopped line "live"
    !Array.isArray(data.points)
  )
    throw Object.assign(new Error('Invalid tick feed'), { name: 'InvalidTickFeed' });
  data.points.forEach((p, i) => {
    if (!Array.isArray(p) || p.length !== 2 || !epoch(p[0]) || !positive(p[1]) || (i > 0 && p[0] <= data.points[i - 1][0]))
      throw new Error('Invalid tick point');
  });
  return data;
};
/** Union by second (`next` wins a conflict), ascending, without seconds before `windowStart`. No interpolation. */
export const mergeTicks = (prev: TickPoint[], next: TickPoint[], windowStart: number): TickPoint[] => {
  const byTime = new Map<number, number>();
  for (const [time, price] of [...prev, ...next]) if (time >= windowStart) byTime.set(time, price);
  return [...byTime].sort((a, b) => a[0] - b[0]);
};
/** The seconds the price chart draws for a round: endAt-900 <= t < endAt (the line never passes endAt). */
export const roundTickPoints = (points: TickPoint[], round: Pick<Round, 'endAt'>) =>
  points.filter(([time]) => time >= round.endAt - TICK_WINDOW_S && time < round.endAt);
/**
 * True when a history line proves the final price of its round. kline1m: the endAt-1 close is the final price.
 * twap60: the TWAP of [endAt-60, endAt), with missing seconds filled (D3), is the final price to the cent (D4).
 * The D3 limit of 10 missing seconds in a row is the engine's settlement gate; the page does not check it.
 */
const provesFinalPrice = (points: TickPoint[], round: Pick<Round, 'endAt' | 'finalPrice' | 'settleMethod'>) => {
  if (settleMethodOf(round) !== 'twap60') return points.at(-1)?.[1] === round.finalPrice;
  const final = round.finalPrice === undefined ? undefined : toCents(round.finalPrice);
  return final !== undefined && twapCents(points, round.endAt) === final;
};
/**
 * /short-markets/ticks/<roundId>.json (BON-276): a resolved round's 1s closes, written once and never rewritten.
 * Returns the seconds the chart draws (roundTickPoints). Any fault throws InvalidTickFeed (permanent; the page keeps
 * the candles): not a tick feed, another round, not final, a first second after max(startAt, endAt-900) (priceMode
 * could not draw it), a round without a final price, no endAt-1 close, or a line that does not prove the final
 * price by the round's settle method (provesFinalPrice).
 */
export const parseRoundHistory = (
  value: unknown,
  round: Pick<Round, 'id' | 'startAt' | 'endAt' | 'finalPrice' | 'settleMethod'>
): TickFeed => {
  const invalid = () => Object.assign(new Error('Invalid round history'), { name: 'InvalidTickFeed' });
  let feed: TickFeed;
  try {
    feed = parseTickFeed(value);
  } catch {
    throw invalid();
  }
  const { roundId, final } = value as { roundId?: unknown; final?: unknown };
  const points = roundTickPoints(feed.points, round);
  const [first, last] = [points[0], points.at(-1)];
  if (
    roundId !== round.id ||
    final !== true ||
    !first ||
    !last ||
    first[0] > Math.max(round.startAt, round.endAt - TICK_WINDOW_S) ||
    last[0] !== round.endAt - 1 ||
    !provesFinalPrice(points, round)
  )
    throw invalid();
  return { ...feed, points };
};
// Building an Intl formatter costs far more than formatting with one, and the chart formats its tick
// labels on every frame: each option set builds its formatter once. The key is the locale and the
// whole option object, so no option can be left out of it. A failed build (an unknown timezone) is not
// kept, so it throws again on the next call, as before.
const DATE_TIME_FORMATS = new Map<string, Intl.DateTimeFormat>();
export const dateTimeFormat = (locale: string, options: Intl.DateTimeFormatOptions) => {
  const key = JSON.stringify([locale, options]);
  let format = DATE_TIME_FORMATS.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    DATE_TIME_FORMATS.set(key, format);
  }
  return format;
};
/** HH:mm in the feed's timezone; HH:mm:ss with `seconds` (the tick line's crosshair). */
export const formatTime = (time: number, timezone: string, seconds = false) =>
  dateTimeFormat('zh-CN', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds && { second: '2-digit' }),
    hour12: false,
  }).format(time * 1000);
const TIMEZONE_LABELS: Record<string, string> = { 'Asia/Singapore': 'SGT' };
export const timezoneLabel = (timezone: string) => {
  return TIMEZONE_LABELS[timezone] ?? timezone;
};
let priceFormat: Intl.NumberFormat | undefined;
/** A BTC price as the 5-minute page prints it: en-US grouping, two decimals, no currency sign. */
export const formatPrice = (price: number) =>
  (priceFormat ??= new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })).format(price);
/** formatPrice, or an em dash while the price is unknown (undefined or null). */
export const formatMoney = (price?: number | null) => (price == null ? '—' : formatPrice(price));
/**
 * A round's market for `token` can be traded on the page once it is deployed and published
 * (ready + pool address), including before the window starts, and until endAt. Stale candles
 * and a missing reference price deliberately do not matter here.
 */
export const isTradable = (round: Round, token: Token, now: number) => {
  const market = round.markets[token];
  // The engine's status closes trading; the clock is an early-close backstop. The pool's end_time is
  // endAt - 1s (prome-vault/tools/dpin-ops/dpin_5m.py build_5m_payload: `rnd["endAt"] - 1`), so
  // the backstop closes there rather than at endAt.
  return (
    !['pending', 'resolved', 'cancelled'].includes(round.status) &&
    !!market?.ready &&
    !!market.marketAddress &&
    now < round.endAt - 1
  );
};
